<template>
  <wizard-form v-bind="ouiaProps" :hidden="hidden" :title="$t('Servizi aggiuntivi')">
    <pf-grid gutter>
      <pf-grid-item sm="6" style="max-width: 70ch" v-if="wizard.askDomainAddon || wizard.askMailAddon || wizard.askAddonSla || wizard.askSsl">
        <wizard-question-yesno
          v-if="wizard.askDomainAddon"
          :title="$t('Vuoi registrare o trasferire un nome a dominio?')"
          v-model="wizard.attivaDominio"
          name="attiva_dominio"
          required
          first-no
        />
        <wizard-question-yesno
          v-if="wizard.askMailAddon"
          :title="$t('Vuoi attivare anche un servizio email?')"
          v-model="wizard.mailAddon"
          name="mail_addon"
          required
          first-no
        >
          <p>{{ $t("L'attivazione di un cloud dedicato non comprende il servizio email, se non viene attivata anche una licenza cPanel o Plesk.") }}</p>
          <p>
            {{
              $t(
                'In questo caso è possibile attivare il servizio email aggiuntivo su hosting condiviso configurato appositamente per la gestione della posta elettronica.',
              )
            }}
          </p>
        </wizard-question-yesno>

        <wizard-question v-if="wizard.askAddonSla" :title="$t('Vuoi attivare l\'assistenza premium?')">
          <template #description>{{
            $t('Supporto premium dedicato, risposte rapide e consulenza tecnica avanzata per garantire continuità e ottimizzazione.')
          }}</template>
          <wizard-question-prodotto v-model="wizard.addonSla" :product="wizard.getProdotto(wizard.codiciProdottiAddon.sla)" show-prezzo />
        </wizard-question>

        <wizard-question v-if="wizard.askSsl" :title="$t('Vuoi attivare un certificato SSL?')">
          <template #description>{{
            $t(
              "Tutti i nostri servizi comprendono l'attivazione di un certificato SSL \"Let's Encrypt\" gratuito per attivare la navigazione cifrata in HTTPS. Oppure puoi scegliere un certificato standard a pagamento con garanzia assicurativa inclusa. Oppure attivare un certificato EV che ti permetterà di rendere più evidente all'interno dei browser questo grado di sicurezza oltre ad avere l'assicurazione in caso di violazione dell'algoritmo. Un certificato EV è particolarmente indicato per tutti i siti di Ecommerce.",
            )
          }}</template>
          <pf-radio
            :label="$t('Certificato Let\'s Encrypt incluso e gratuito')"
            name="codiceSsl"
            value=""
            :checked="wizard.codiceSsl === ''"
            @change="wizard.codiceSsl = ''"
          >
            <template #label>
              {{ $t("Certificato Let's Encrypt") }}
              <wizard-price-tag />
            </template>
            <template #description>
              {{ $t('Incluso e gratuito in tutti i nostri servizi, senza costi né configurazioni aggiuntive richieste') }}
            </template>
          </pf-radio>

          <wizard-question-prodotti v-model="wizard.codiceSsl" :prodotti="wizard.codiciProdottiAddon.ssl" name="codiceSsl" show-feature required />
        </wizard-question>

        <template v-if="wizard.path === 'altro'">
          <wizard-question :title="$t('Vuoi attivare una licenza terminal server?')">
            <wizard-question-prodotto
              v-model="wizard.addonTerminalServer"
              v-model:qt="wizard.terminalServerQt"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.terminalServer)"
              :group-required="!noAddonSelected"
              show-prezzo
              ask-qt
            />
          </wizard-question>

          <wizard-question :title="$t('Vuoi attivare un il servizio Atlas di archiviazione delle mail?')">
            <wizard-question-prodotto
              v-model="wizard.addonAtlas"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.atlas)"
              :group-required="!noAddonSelected"
              show-prezzo
            />
          </wizard-question>
        </template>
      </pf-grid-item>

      <pf-grid-item sm="6" style="max-width: 70ch">
        <wizard-question
          v-if="
            (!wizard.askDomainFirst && ((wizard.askDomainAddon && wizard.attivaDominio) || (wizard.askMailAddon && wizard.mailAddon))) ||
            wizard.path === 'storage' ||
            wizard.path === 'altro'
          "
          :title="$t('Qual è il nome a dominio, o sottodominio, del progetto?')"
          style="max-width: 100ch"
        >
          <wizard-question-nome-dominio v-model="wizard.nomeDominio" name="nome_dominio" :show-prezzo="wizard.askDomainAddon && wizard.attivaDominio" required />
        </wizard-question>

        <wizard-question v-if="wizard.askMailAddon && wizard.mailAddon" :title="$t('Scegli il prodotto per la gestione delle email')">
          <wizard-question-prodotti
            v-model="wizard.codiceProdottoMail"
            :prodotti="wizard.codiciProdottiInfrastruttura.mail"
            name="sceltaProdottoMail"
            show-feature
            variant="list"
            required
          />
        </wizard-question>
      </pf-grid-item>
    </pf-grid>
  </wizard-form>
</template>

<style scoped>
:deep(.features .list-view-pf-additional-info-item) {
  width: 100%;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue';
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '@/wizard';

import WizardQuestion from '../questions/wizard-question.vue';
import WizardQuestionYesno from '../questions/wizard-question-yesno.vue';
import WizardQuestionProdotti from '../questions/wizard-question-prodotti.vue';
import WizardQuestionProdotto from '../questions/wizard-question-prodotto.vue';
import wizardQuestionNomeDominio from '../questions/wizard-question-nome-dominio.vue';
import WizardForm from '../wizard-form.vue';
import WizardPriceTag from '../wizard-price-tag.vue';

defineOptions({
  name: 'WizardStepAddon',
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep({
  isEnabled: wizard =>
    !wizard.modal &&
    (wizard.askDomainAddon || wizard.askMailAddon || wizard.path === 'storage' || wizard.path === 'altro' || wizard.askAddonSla || wizard.askSsl),
});

defineExpose({
  isEnabled,
});

const noAddonSelected = computed(() => wizard.path === 'altro' && !(wizard.addonTerminalServer || wizard.codiceSsl || wizard.addonAtlas));
</script>
